import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Copy, Pencil, RotateCcw, Save, Search, Trash2 } from "lucide-react";
import { toast } from "../../../../utils/toast";
import { Badge } from "../../../../components/ui/Badge";
import { Button } from "../../../../components/ui/Button";
import { Tooltip } from "../../../../components/ui/Tooltip";
import {
  putBaseRoleOverride,
  updateRole,
  rolesErrorMessage,
} from "../../../../api/roles";
import { ConfirmModal, InlineError } from "./RoleDialogs";
import { ROLE_KINDS, diffPermissions, filterGroups, roleKindLabel } from "./rolesModel";

/**
 * Casilla de un permiso. Si el que edita no puede otorgarlo (`grantable:false`)
 * queda bloqueada con la razón en un tooltip enfocable por teclado: un input
 * `disabled` no recibe foco ni hover, así que el tooltip va en un wrapper.
 */
function PermissionCheckbox({ entry, label, checked, editable, onToggle }) {
  const { t } = useTranslation();
  const blocked = editable && entry.grantable === false;
  const input = (
    <input
      type="checkbox"
      className="h-4 w-4 cursor-pointer accent-brand-600 disabled:cursor-not-allowed"
      checked={checked}
      disabled={!editable || blocked}
      onChange={() => onToggle(entry.key)}
      aria-label={label}
    />
  );
  if (!blocked) return input;
  return (
    <Tooltip content={t("No puedes otorgar este permiso: no está disponible para los roles de tu empresa o tu cuenta no lo tiene.")}>
      <span tabIndex={0} className="inline-flex rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500">
        {input}
      </span>
    </Tooltip>
  );
}

/**
 * Editor de los permisos de UN rol. El padre lo monta con `key` = rol + firma
 * de sus permisos guardados, así el borrador se reinicia solo al cambiar de rol
 * o tras guardar (sin efectos de sincronización).
 */
export default function RoleEditor({
  role, groups, catalogIndex, canUpdate, canCreate, canDelete,
  onSaved, onDirtyChange, onRename, onDuplicate, onDelete, onReset,
}) {
  const { t } = useTranslation();
  const [draft, setDraft] = useState(() => new Set(role.permissions));
  const [search, setSearch] = useState("");
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [confirmOpen, setConfirmOpen] = useState(false);

  const { added, removed } = useMemo(() => diffPermissions(role.permissions, draft), [role.permissions, draft]);
  const changes = added.length + removed.length;
  const editable = !!role.editable && canUpdate;
  const visibleGroups = useMemo(() => filterGroups(groups, search), [groups, search]);

  useEffect(() => { onDirtyChange(changes > 0); }, [changes, onDirtyChange]);
  // Al desmontar (cambio de rol o guardado) el padre vuelve a "sin cambios".
  useEffect(() => () => onDirtyChange(false), [onDirtyChange]);

  // Permisos que el rol ya tiene y quien edita no puede otorgar: se muestran
  // bloqueados y viajan en el guardado tal cual (el backend decide).
  const heldNotGrantable = role.permissions.filter((k) => catalogIndex.get(k)?.grantable !== true);

  const toggle = (key) => {
    setDraft((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  const save = async () => {
    setSaving(true);
    setSaveError("");
    const permissions = Array.from(draft).sort();
    const result = role.kind === ROLE_KINDS.CUSTOM
      ? await updateRole(role.id, { permissions })
      : await putBaseRoleOverride(role.baseRoleId, permissions);
    setSaving(false);
    setConfirmOpen(false);
    if (result.error) {
      setSaveError(rolesErrorMessage(result.error, t, t("No se pudieron guardar los permisos. Intenta de nuevo.")));
      return;
    }
    toast.success(t("Permisos guardados"));
    onSaved(role);
  };

  // Quitar permisos a un rol con usuarios les corta accesos al renovar sesión: se confirma.
  const requestSave = () => {
    if (removed.length > 0 && role.userCount > 0) setConfirmOpen(true);
    else save();
  };

  const isBase = role.kind === ROLE_KINDS.BASE;
  const canManageRole = !!role.editable;

  return (
    <div className="min-w-0 p-4 sm:p-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="flex flex-wrap items-center gap-2 text-base font-semibold text-fg">
            <span className="break-words">{role.name}</span>
            <Badge tone={role.kind === ROLE_KINDS.CUSTOM ? "info" : "neutral"}>{roleKindLabel(role.kind, t)}</Badge>
            {role.kind === ROLE_KINDS.OVERRIDE && <Badge tone="warning">{t("Personalizado")}</Badge>}
          </h3>
          {role.description ? <p className="mt-1 text-sm text-fg-muted">{role.description}</p> : null}
          <p className="mt-1 text-xs text-fg-muted">
            {role.kind === ROLE_KINDS.CUSTOM
              ? t("Rol base: {{base}}", { base: role.baseRoleName })
              : null}
            {role.kind === ROLE_KINDS.CUSTOM ? " · " : ""}
            {t("Usuarios: {{n}}", { n: role.userCount ?? 0 })}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {canManageRole && role.kind === ROLE_KINDS.CUSTOM && canUpdate && (
            <Button variant="outline" size="sm" iconLeft={<Pencil className="h-4 w-4" />} onClick={() => onRename(role)}>
              {t("Renombrar")}
            </Button>
          )}
          {canManageRole && canCreate && (
            <Button variant="outline" size="sm" iconLeft={<Copy className="h-4 w-4" />} onClick={() => onDuplicate(role)}>
              {t("Duplicar")}
            </Button>
          )}
          {canManageRole && role.kind === ROLE_KINDS.OVERRIDE && canUpdate && (
            <Button variant="outline" size="sm" iconLeft={<RotateCcw className="h-4 w-4" />} onClick={() => onReset(role)}>
              {t("Volver a la plantilla")}
            </Button>
          )}
          {canManageRole && role.kind === ROLE_KINDS.CUSTOM && canDelete && (
            <Button variant="outline" size="sm" iconLeft={<Trash2 className="h-4 w-4" />} onClick={() => onDelete(role)}>
              {t("Eliminar")}
            </Button>
          )}
        </div>
      </div>

      {!role.editable && (
        <p role="note" className="mt-3 text-xs text-fg-muted">
          {t("Este rol solo se puede consultar: no se puede editar ni asignar desde tu cuenta.")}
        </p>
      )}
      {editable && isBase && (
        <p role="note" className="mt-3 text-xs text-fg-muted">
          {t("Al guardar se crea una personalización de este rol solo para tu empresa. La plantilla global no cambia.")}
        </p>
      )}
      {editable && heldNotGrantable.length > 0 && (
        <p role="note" className="mt-3 text-xs text-status-warning">
          {t("Este rol incluye {{n}} permisos que no puedes otorgar; se muestran bloqueados.", { n: heldNotGrantable.length })}
        </p>
      )}

      <div className="mt-4 flex flex-wrap items-center gap-2">
        <div className="relative w-full sm:w-72">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-fg-muted" aria-hidden="true" />
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t("Buscar módulo, recurso o acción…")}
            aria-label={t("Buscar permisos")}
            className="h-9 w-full rounded-lg border border-border bg-bg pl-9 pr-3 text-sm text-fg focus:outline-none focus:ring-2 focus:ring-brand-500"
          />
        </div>
        <span className="text-xs text-fg-muted" aria-live="polite">
          {changes > 0 ? t("Cambios sin guardar: {{n}}", { n: changes }) : t("Sin cambios")}
        </span>
        {editable && (
          <div className="ml-auto flex items-center gap-2">
            <Button variant="outline" size="sm" disabled={changes === 0 || saving} onClick={() => { setDraft(new Set(role.permissions)); setSaveError(""); }}>
              {t("Descartar")}
            </Button>
            <Button size="sm" iconLeft={<Save className="h-4 w-4" />} disabled={changes === 0} loading={saving} onClick={requestSave}>
              {t("Guardar")}
            </Button>
          </div>
        )}
      </div>

      <div className="mt-3"><InlineError>{saveError}</InlineError></div>

      <div className="mt-4 space-y-5">
        {visibleGroups.length === 0 ? (
          <p className="text-sm text-fg-muted">{t("Nada coincide con la búsqueda.")}</p>
        ) : (
          visibleGroups.map((group) => (
            <section key={group.module || "otros"} aria-label={group.label}>
              <h4 className="mb-2 text-sm font-semibold text-fg">{group.label}</h4>
              {/* El scroll horizontal es de la matriz: en pantallas angostas no ensancha la página. */}
              <div
                className="overflow-x-auto rounded-lg border border-border"
                role="region"
                aria-label={t("Permisos de {{modulo}}", { modulo: group.label })}
                tabIndex={0}
              >
                <table className="min-w-full divide-y divide-border text-sm">
                  <thead className="bg-bg-muted">
                    <tr>
                      <th scope="col" className="sticky left-0 bg-bg-muted px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wider text-fg-muted">
                        {t("Recurso")}
                      </th>
                      {group.actions.map((a) => (
                        <th key={a.action} scope="col" className="px-3 py-2 text-center text-[11px] font-semibold uppercase tracking-wider text-fg-muted whitespace-nowrap">
                          {a.label}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {group.resources.map((res) => (
                      <tr key={res.resource}>
                        <th scope="row" className="sticky left-0 bg-bg px-3 py-2 text-left font-medium text-fg whitespace-nowrap">
                          {res.label}
                        </th>
                        {group.actions.map((a) => {
                          const entry = res.actions.find((x) => x.action === a.action);
                          return (
                            <td key={a.action} className="px-3 py-2 text-center">
                              {entry ? (
                                <PermissionCheckbox
                                  entry={entry}
                                  label={`${res.label}: ${a.label}`}
                                  checked={draft.has(entry.key)}
                                  editable={editable}
                                  onToggle={toggle}
                                />
                              ) : (
                                <span aria-hidden="true" className="text-xs text-fg-subtle">—</span>
                              )}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          ))
        )}
      </div>

      <ConfirmModal
        open={confirmOpen}
        title={t("Quitar permisos")}
        confirmLabel={t("Quitar y guardar")}
        busy={saving}
        onConfirm={save}
        onCancel={() => setConfirmOpen(false)}
      >
        <p>
          {t("Vas a quitar {{n}} permisos a un rol que usan {{users}} usuarios. Perderán ese acceso cuando se renueve su sesión (máximo 1 hora).", { n: removed.length, users: role.userCount })}
        </p>
        <ul className="max-h-40 list-disc space-y-0.5 overflow-y-auto pl-5 text-xs text-fg-muted">
          {removed.map((key) => <li key={key}>{catalogIndex.get(key)?.label || key}</li>)}
        </ul>
      </ConfirmModal>
    </div>
  );
}
